import React, { useEffect, useMemo, useRef, useState } from 'react'
import './Gallery.css'
import gallery1 from '../../Assets/custom-site/gallery1.png'
import gallery2 from '../../Assets/custom-site/gallery2.png'
import gallery3 from '../../Assets/custom-site/gallery3.png'
import gallery4 from '../../Assets/custom-site/gallery4.png'
import axios from 'axios'
import { useParams } from 'react-router-dom'

const Gallery = ({ colourScheme, hashtag = "" }) => {
    const { friendlyURL } = useParams()
    const galleryImages = [gallery1, gallery2, gallery3, gallery4];
    const sliderRef = useRef(null);

    const [statusLoading, setStatusLoading] = useState(true)
    const [status, setStatus] = useState({ isAvailable: false, reason: null, maxImages: 3 })

    const [from, setFrom] = useState("")
    const [message, setMessage] = useState("")
    const [files, setFiles] = useState([])
    const [submitLoading, setSubmitLoading] = useState(false)
    const [submitError, setSubmitError] = useState("")
    const [submitSuccess, setSubmitSuccess] = useState(false)
    const [showModal, setShowModal] = useState(false)

    const maxImages = status?.maxImages || 3

    useEffect(() => {
        let mounted = true
        setStatusLoading(true)

        axios.post(`${process.env.REACT_APP_API}/guest-gallery/status-by-url`, { friendlyURL })
            .then((res) => {
                if (!mounted) return
                setStatus(res.data || { isAvailable: false, reason: "serverError", maxImages: 3 })
                setStatusLoading(false)
            })
            .catch(() => {
                if (!mounted) return
                setStatus({ isAvailable: false, reason: "serverError", maxImages: 3 })
                setStatusLoading(false)
            })

        return () => { mounted = false }
    }, [friendlyURL])

    const availabilityMessage = useMemo(() => {
        if (statusLoading) return "Checking if uploads are open..."
        if (status?.isAvailable) return null

        const r = status?.reason
        if (r === "notWeddingDay") return "Guest Gallery uploads are only open on the wedding day."
        if (r === "notPremium") return "Guest Gallery is not available for this wedding."
        if (r === "weddingNotFound") return "Wedding not found."
        if (r === "weddingDateMissing") return "Uploads aren’t available yet."
        return "Uploads are not available right now."
    }, [statusLoading, status])

    const scrollLeft = () => {
        if (sliderRef.current) {
            sliderRef.current.scrollBy({ left: -310, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (sliderRef.current) {
            sliderRef.current.scrollBy({ left: 310, behavior: 'smooth' });
        }
    };

    const onPickFiles = (e) => {
        setSubmitError("")
        setSubmitSuccess(false)

        const list = Array.from(e.target.files || [])
        const next = list.slice(0, maxImages)
        setFiles(next)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitError("")
        setSubmitSuccess(false)

        const trimmedFrom = String(from || "").trim()
        const trimmedMessage = String(message ?? "")

        if (!trimmedFrom) {
            setSubmitError("Please add who the photos are from.")
            return
        }
        if (files.length < 1) {
            setSubmitError("Please choose at least 1 photo.")
            return
        }
        if (files.length > maxImages) {
            setSubmitError(`You can upload up to ${maxImages} photos.`)
            return
        }

        try {
            setSubmitLoading(true)

            const form = new FormData()
            form.append("friendlyURL", friendlyURL)
            form.append("from", trimmedFrom)
            form.append("message", trimmedMessage)
            files.forEach((f) => form.append("images", f))

            await axios.post(`${process.env.REACT_APP_API}/guest-gallery/submit-by-url`, form, {
                headers: { "Content-Type": "multipart/form-data" }
            })

            setSubmitSuccess(true)
            setFrom("")
            setMessage("")
            setFiles([])

            // reset input element value (so selecting same file again works)
            if (e?.target?.elements?.images) {
                e.target.elements.images.value = ""
            }
        } catch (err) {
            const msg =
                err?.response?.data?.error ||
                "Upload failed. Please try again."
            setSubmitError(msg)
        } finally {
            setSubmitLoading(false)
        }
    }

    const fileNamesLabel = useMemo(() => {
        if (!files || files.length === 0) return "No files selected"
        if (files.length === 1) return files[0]?.name || "1 file selected"
        return `${files.length} files selected`
    }, [files])

    return (
        <div className={`gallery-container ${colourScheme}-gallery`}>
            <h2 className="gallery-title">Guest Gallery</h2>

            {hashtag && (
                <p className="gallery-hashtag">#{hashtag.toUpperCase()}</p>
            )}

            <p className="gallery-description">
                Help us capture the magic of our day through your eyes. On the wedding day, you'll be able to upload your favourite moments, from joyful laughter to dance floor elegance, to our guest gallery below.
                <br /><br />
                We can't wait to see the celebration as you experienced it and treasure those memories forever.
            </p>

            <div
                className={`gallery-upload-area ${colourScheme}-gallery-border guest-gallery-clickable`}
                onClick={() => {
                    if (!status?.isAvailable || availabilityMessage) return;
                    setShowModal(true);
                    setSubmitError("");
                    setSubmitSuccess(false);
                }}
            >
                <img src={require(`../../Assets/custom-site/${colourScheme}/upload.png`)} alt="Upload" className="upload-icon" />
                <p className="upload-text">Upload your photos here</p>
                {availabilityMessage && (
                    <p className="guest-gallery-status">{availabilityMessage}</p>
                )}
                {!availabilityMessage && (
                    <p className="guest-gallery-hint">
                        Tap to upload up to {maxImages} photos on the wedding day.
                    </p>
                )}
            </div>

            {showModal && (
                <div className="guest-gallery-modal-overlay" onClick={() => !submitLoading && setShowModal(false)}>
                    <div className="guest-gallery-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="guest-gallery-modal-header">
                            <h3>Upload photos</h3>
                            <button
                                type="button"
                                className="guest-gallery-modal-close"
                                onClick={() => !submitLoading && setShowModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        {availabilityMessage ? (
                            <p className="guest-gallery-status" style={{ marginBottom: 0 }}>
                                {availabilityMessage}
                            </p>
                        ) : (
                            <form className="guest-gallery-form" onSubmit={handleSubmit}>
                                <div className="guest-gallery-fields">
                                    <div className="guest-gallery-field">
                                        <label>From</label>
                                        <input
                                            type="text"
                                            value={from}
                                            onChange={(e) => setFrom(e.target.value)}
                                            placeholder="e.g. Sarah & Tom"
                                            maxLength={60}
                                            disabled={submitLoading}
                                            required
                                        />
                                    </div>

                                    <div className="guest-gallery-field">
                                        <label>Message (optional)</label>
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Leave a message for the couple"
                                            maxLength={500}
                                            disabled={submitLoading}
                                            rows={3}
                                        />
                                    </div>

                                    <div className="guest-gallery-field">
                                        <label>Photos (up to {maxImages})</label>
                                        <input
                                            name="images"
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={onPickFiles}
                                            disabled={submitLoading}
                                        />
                                        <p className="guest-gallery-hint">{fileNamesLabel}</p>
                                    </div>

                                    {submitError ? <p className="guest-gallery-error">{submitError}</p> : null}
                                    {submitSuccess ? <p className="guest-gallery-success">Thanks! Your photos have been uploaded.</p> : null}

                                    <button className="guest-gallery-submit" type="submit" disabled={submitLoading}>
                                        {submitLoading ? "Uploading..." : "Submit"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            <div className="gallery-slider">
                <button className="slider-arrow left-arrow" onClick={scrollLeft} aria-label="Previous">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <div className="gallery-images" ref={sliderRef}>
                    {galleryImages.map((img, index) => (
                        <div key={index} className="gallery-image-wrapper">
                            <img
                                src={img}
                                alt={`Gallery ${index + 1}`}
                                className="gallery-image"
                            />
                            <div className="gallery-overlay"></div>
                        </div>
                    ))}
                </div>

                <button className="slider-arrow right-arrow" onClick={scrollRight} aria-label="Next">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    )
}

export default Gallery